import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Field, Forms } from '@ofself/shell';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/common';
import { isSafeRedirectUrl } from '../utils/redirectSafety';
import { NUCLEUS_URL } from '../services/apiConfig';
import '../styles/Auth.css';

function Login() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  // An account whose signup stopped before its phone was verified: that is
  // finished in Nucleus, where signup lives (pages/Register).
  const [unfinished, setUnfinished] = useState(false);
  const { login } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Show expired message as toast, then clear from URL
  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      toast.info('Your session has expired. Please log in again.');
      // Remove 'expired' from URL without triggering navigation
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('expired');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, toast]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnfinished(false);

    try {
      await login(identifier, password);

      toast.success('Logged in successfully!');

      // Check if there's a return URL (for authorization flow)
      // Support both 'next' (OAuth standard) and 'returnUrl' (legacy)
      const nextUrl = searchParams.get('next') || searchParams.get('returnUrl');
      if (nextUrl && isSafeRedirectUrl(nextUrl)) {
        // If it's a full URL (third-party authorization), redirect there
        if (nextUrl.startsWith('http://') || nextUrl.startsWith('https://')) {
          window.location.href = nextUrl;
        } else {
          navigate(decodeURIComponent(nextUrl));
        }
      } else {
        navigate('/home');
      }
    } catch (err) {
      if (err?.code === 'PHONE_VERIFICATION_REQUIRED') {
        setUnfinished(true);
        return;
      }
      const message = err.message || 'Login failed';
      // Show specific messages for common errors
      if (message.toLowerCase().includes('invalid') || message.toLowerCase().includes('credentials') || message.toLowerCase().includes('password')) {
        toast.error('Invalid email/username or password. Please try again.');
      } else if (message.toLowerCase().includes('not found') || message.toLowerCase().includes('no user') || message.toLowerCase().includes('no account')) {
        toast.error('No account found. Please check or register.');
      } else if (message.toLowerCase().includes('network')) {
        toast.error('Unable to connect to server. Please check your connection.');
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // The kit's form-folio — the template this page originated (uprooted into
  // @ofself/shell 0.75.0 as <Forms>). The app owns state, validation and
  // routing; the kit owns the geometry and the voice.
  return (
    <Field>
      <Forms
        statement="Sign in to OfSelf"
        mark={<img src="/ofself-logo.svg" alt="" />}
        onSubmit={handleSubmit}
      >
        <Forms.Field label="Email or Username" htmlFor="identifier" required>
          <input
            id="identifier"
            type="text"
            autoComplete="username"
            placeholder="your@email.com or username"
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
          />
        </Forms.Field>

        <Forms.Field label="Password" htmlFor="password" required>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Forms.Field>

        {unfinished && (
          <Forms.Block>
            <p className="auth-help" role="alert">
              Your account isn’t finished yet: it needs a verified phone.{' '}
              <a href={`${NUCLEUS_URL}/login`}>Finish signing up in Nucleus</a>, then sign in here.
            </p>
          </Forms.Block>
        )}

        <Forms.Cta busy={loading}>
          {loading ? 'Signing in...' : 'Sign in'}
        </Forms.Cta>

        <Forms.Foot>
          <Forms.FootCell href="/forgot-password" onNavigate={navigate}>
            <strong>Forgot password?</strong>
          </Forms.FootCell>
          <Forms.FootCell href="/register" onNavigate={navigate}>
            <strong>Create your OfSelf account↗</strong>
          </Forms.FootCell>
        </Forms.Foot>
      </Forms>
    </Field>
  );
}

export default Login;
